import React, { useState, useEffect, useMemo, useRef } from 'react';
import Select from 'react-select';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { debounce } from 'lodash';
import { FormGroup, FormGroupLabel, ErrorMessage, RequiredMark } from './BaseInput';
import { colorAccent, colorAdditional, colorBackground, colorFieldsBorder, colorIcon, colorText, colorBackgroundBody } from '../../theme/palette';
import { applyTextStyle } from '../../theme/typography';

const getMenuPortalTarget = () => (typeof document !== 'undefined' ? document.body : null);
const SELECT_ALL_VALUE = '__select_all__';

const SearchSelect = ({
  name,
  label,
  placeholder,
  options = [],
  value,
  onChange,
  disabled = false,
  required = false,
  error,
  onSearch,
  minSearchLength = 2,
  maxResults = 10,
  input,
  meta,
  isMulti = false,
  showSelectAll = false,
  customOnChange,
  ...props
}) => {
  // Usar input.value e input.onChange se disponível (React Final Form)
  const fieldValue = input ? input.value : value;
  const fieldOnChange = input ? input.onChange : onChange;
  const fieldName = input ? input.name : name;
  const [searchOptions, setSearchOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const searchRequestRef = useRef(0);
  // Debounced search function

  const customStyles = useMemo(() => ({
    menuPortal: base => ({
      ...base,
      zIndex: 9999
    }),
    menu: base => ({
      ...base,
      minWidth: '100%',
      width: 'max-content',
      maxWidth: 'min(90vw, 480px)'
    }),
    menuList: base => ({
      ...base,
      maxHeight: 260,
      overflowY: 'auto',
      overflowX: 'hidden'
    }),
    option: (base, state) => {
      const isSelectAllOption = state.data?.value === SELECT_ALL_VALUE;

      if (isSelectAllOption) {
        return {
          ...base,
          whiteSpace: 'nowrap',
          backgroundColor: colorBackground,
          color: colorAccent,
          fontWeight: 700,
          borderBottom: '1px solid #575454',
          marginBottom: 6,
          paddingBottom: 10
        };
      }

      return {
        ...base,
        whiteSpace: 'nowrap'
      };
    }
  }), []);

  const debouncedSearch = useMemo(() => debounce(async inputValue => {
    if (inputValue.length >= minSearchLength && onSearch) {
      const requestId = searchRequestRef.current + 1;
      searchRequestRef.current = requestId;
      setIsLoading(true);

      try {
        const results = await onSearch(inputValue);
        if (requestId !== searchRequestRef.current) return;

        const formattedResults = results.slice(0, maxResults).map(item => ({
          value: item.value || item.id || item,
          label: item.label || item.nome || item.name || item,
          data: item.data || item // Incluir dados completos se disponível

        }));
        setSearchOptions(formattedResults);
      } catch (error) {
        if (requestId !== searchRequestRef.current) return;

        console.error('Erro na busca:', error);
        setSearchOptions([]);
      } finally {
        if (requestId === searchRequestRef.current) {
          setIsLoading(false);
        }
      }
    } else {
      searchRequestRef.current += 1;
      setSearchOptions([]);
      setIsLoading(false);
    }
  }, 300), [onSearch, minSearchLength, maxResults]); // Handle input change for search

  const handleInputChange = inputValue => {
    if (inputValue.length >= minSearchLength) {
      debouncedSearch(inputValue);
    } else {
      searchRequestRef.current += 1;
      debouncedSearch.cancel();
      setSearchOptions([]);
      setIsLoading(false);
    }

    return inputValue;
  }; // Handle option selection

  const handleChange = selectedOption => {
    if (isMulti && Array.isArray(selectedOption)) {
      const shouldToggleSelectAll = selectedOption.some(option => option?.value === SELECT_ALL_VALUE);

      if (shouldToggleSelectAll) {
        const nextValue = allSelected ? [] : allOptions;

        if (fieldOnChange) {
          fieldOnChange(nextValue);
        }

        if (customOnChange) {
          customOnChange(nextValue);
        }

        return;
      }
    }

    if (fieldOnChange) {
      fieldOnChange(selectedOption);
    } // Se houver um callback personalizado, chamá-lo também


    if (customOnChange) {
      customOnChange(selectedOption);
    }
  }; // Format current value for react-select


  const currentValue = useMemo(() => {
    if (!fieldValue) return null;

    if (isMulti) {
      if (Array.isArray(fieldValue)) return fieldValue;
      if (typeof fieldValue === 'object' && fieldValue.value !== undefined) return [fieldValue];
      if (typeof fieldValue === 'string') {
        return [{
          value: fieldValue,
          label: fieldValue
        }];
      }

      return [];
    } else {
      if (typeof fieldValue === 'object' && fieldValue.value) {
        return fieldValue;
      } else if (typeof fieldValue === 'string') {
        return {
          value: fieldValue,
          label: fieldValue
        };
      }

      return null;
    }
  }, [fieldValue, isMulti]); // Combine static options with search results

  const allOptions = useMemo(() => {
    const staticOptions = options.map(option => ({
      value: option.value || option.id || option,
      label: option.label || option.nome || option.name || option
    })); // Filter out duplicates

    const searchOptionsFiltered = searchOptions.filter(searchOption => !staticOptions.some(staticOption => staticOption.value === searchOption.value));
    const combinedOptions = [...staticOptions, ...searchOptionsFiltered];
    return combinedOptions;
  }, [options, searchOptions]); // Cleanup debounced function

  const allSelected = useMemo(() => {
    if (!isMulti || !allOptions.length) return false;
    if (!Array.isArray(currentValue) || !currentValue.length) return false;

    const selectedValues = new Set(currentValue.map(option => String(option?.value)));
    return allOptions.every(option => selectedValues.has(String(option?.value)));
  }, [allOptions, currentValue, isMulti]);
  const selectedCount = Array.isArray(currentValue) ? currentValue.length : 0;
  const shouldExpand = isMulti && isExpanded;
  const singleSelectedLabel = selectedCount === 1 ? currentValue?.[0]?.label : '';
  const summaryLabel = selectedCount === 1
    ? singleSelectedLabel
    : `${selectedCount} selecionados`;
  const resolvedPlaceholder = isMulti && selectedCount > 0
    ? summaryLabel
    : (placeholder || 'Digite para buscar...');
  const resolvedOptions = useMemo(() => {
    if (!isMulti || !showSelectAll || !allOptions.length) {
      return allOptions;
    }

    return [
      {
        value: SELECT_ALL_VALUE,
        label: allSelected ? 'Limpar selecao' : 'Selecionar todos'
      },
      ...allOptions
    ];
  }, [allOptions, allSelected, isMulti, showSelectAll]); // Cleanup debounced function

  useEffect(() => {
    return () => {
      searchRequestRef.current += 1;
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  return <FormGroup>
      {label && <FormGroupLabel>
          {label}
          {required && <RequiredMark>*</RequiredMark>}
        </FormGroupLabel>}
      <SelectAnchor isExpanded={shouldExpand}>
        <StyledSelect name={fieldName} value={currentValue} onChange={handleChange} onMenuOpen={() => {
          setIsExpanded(true);
        }} onMenuClose={() => {
          setIsExpanded(false);
        }} options={resolvedOptions} onInputChange={handleInputChange} placeholder={resolvedPlaceholder} isDisabled={disabled} isLoading={isLoading} isMulti={isMulti} isClearable={!required} isSearchable={true} closeMenuOnSelect={!isMulti} hideSelectedOptions={false} controlShouldRenderValue={!isMulti} isOptionSelected={isMulti ? (option, selectValue) => {
          if (option?.value === SELECT_ALL_VALUE) return false;

          return Array.isArray(selectValue) && selectValue.some(selectedOption => String(selectedOption?.value) === String(option?.value));
        } : undefined} className="react-select" classNamePrefix="react-select" noOptionsMessage={() => "Nenhum resultado encontrado"} loadingMessage={() => "Carregando..."} menuPortalTarget={getMenuPortalTarget()} menuPosition="fixed" menuPlacement="auto" maxMenuHeight={260} styles={customStyles} isExpanded={shouldExpand} {...props} />
      </SelectAnchor>
      {(error || meta && meta.touched && meta.error) && <ErrorMessage>
          {error || meta.error}
        </ErrorMessage>}
    </FormGroup>;
};

SearchSelect.propTypes = {
  name: PropTypes.string,
  label: PropTypes.node,
  placeholder: PropTypes.string,
  options: PropTypes.array,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  onSearch: PropTypes.func,
  minSearchLength: PropTypes.number,
  maxResults: PropTypes.number,
  input: PropTypes.object,
  meta: PropTypes.object,
  isMulti: PropTypes.bool,
  showSelectAll: PropTypes.bool,
  customOnChange: PropTypes.func
};
export default SearchSelect; // region STYLES

const SelectAnchor = styled.div`
  position: relative;
  width: 100%;
  height: 32px;
  z-index: ${({ isExpanded }) => (isExpanded ? 20 : 'auto')};
  overflow: visible;
`;

const StyledSelect = styled(Select)`
  width: 100%;
  position: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'absolute' : 'relative')};
  top: 0;
  left: 0;
  right: 0;
  z-index: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 21 : 'auto')};
  ${applyTextStyle('body')}

  .react-select__control {
    min-height: 32px;
    height: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'auto' : '32px')};
    box-sizing: border-box;
    border-radius: 0 !important;
    transition: all 0.3s;
    border: 1px solid ${colorFieldsBorder};
    background-color: ${colorBackground};
    align-items: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'flex-start' : 'center')};
    overflow: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'visible' : 'hidden')};

    &.react-select__control--is-focused, &:hover {
      border-color: ${colorAccent} !important;
      box-shadow: none;
      background: ${colorBackground};
      background-color: ${colorBackground};
    }

    &.react-select__control--is-disabled {
      background-color: ${colorBackgroundBody}; 
      border-color: ${colorFieldsBorder};
    }
  }

  .react-select__input {
    height: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'auto' : '30px')};
    color: ${colorText};
    ${applyTextStyle('body')}
  }

  .react-select__indicators {
    height: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'auto' : '30px')};
    align-items: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'flex-start' : 'center')};
    flex-shrink: 0;
  }

  .react-select__indicator-separator {
    display: none;
  }

  .react-select__dropdown-indicator, .react-select__clear-indicator {
    padding: 6px;
    cursor: pointer;
    color: ${colorIcon};
    transition: transform 0.2s ease;

    svg {
      height: 16px;
      width: 16px;
    }
  }

  .react-select__control--menu-is-open .react-select__dropdown-indicator {
    transform: rotate(180deg);
  }

  .react-select__multi-value {
    background-color: transparent;
    border: 1px solid ${colorAccent};

    .react-select__multi-value__label {
      padding: 3px 6px;
      border-right: 1px solid ${colorAccent};
      color: ${colorText};
      ${applyTextStyle('caption')}
    }
  }

  .react-select__multi-value__remove {
    opacity: 0.8;
    transition: 0.3s;
    cursor: pointer;
    color: ${colorText};
  }

  .react-select__multi-value__label, .react-select__multi-value__remove {
    background: ${colorBackground};
  }

  .react-select__single-value {
    color: ${colorText};
    ${applyTextStyle('body')}
  }

  .react-select__menu {
    box-shadow: none !important;
    margin-top: 6px;
    margin-bottom: 6px;
    z-index: 22;
  }

  .react-select__menu-list {
    top: calc(100% + 1px);
    border-radius: 0;
    box-shadow: none;
    ${applyTextStyle('body')}
    overflow-y: auto;
    overflow-x: hidden;
    max-height: 260px;
    background: ${colorBackground};
    border: 1px solid ${colorFieldsBorder};
  }

  .react-select__option {
    padding: 6px 10px;
    cursor: pointer;
    transition: background-color 0.2s;
    ${applyTextStyle('body')}
    white-space: nowrap;

    &:hover {
      background-color: ${colorFieldsBorder};
    }

    &.react-select__option--is-focused {
      background-color: ${colorFieldsBorder};
    }

    &.react-select__option--is-selected {
      background-color: ${colorAccent};
      color: white;
    }
  }

  .react-select__placeholder {
    ${applyTextStyle('body')}
    color: ${colorAdditional};
    margin-top: -2px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  
  .react-select__value-container {
    background: ${colorBackground};
    min-height: 30px;
    height: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'auto' : '30px')};
    box-sizing: border-box;
    padding-top: 0;
    padding-bottom: 0;
    padding-left: 10px;
    padding-right: 10px;
    flex-wrap: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'wrap' : 'nowrap')};
    align-items: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'flex-start' : 'center')};
    overflow: ${({ isMulti, isExpanded }) => (isMulti && isExpanded ? 'visible' : 'hidden')};
    min-width: 0;
    
    & > div {
      margin-top: 0;
      margin-bottom: 0;
    }
  }

  .react-select__single-value,
  .react-select__input-container {
    min-width: 0;
  }

  .react-select__input-container {
    overflow: hidden;
  }

  .react-select__value-container--is-multi {
    padding-top: ${({ isExpanded }) => (isExpanded ? '2px' : '0')};
    padding-bottom: ${({ isExpanded }) => (isExpanded ? '2px' : '0')};
  }

  .react-select__multi-value {
    margin-top: ${({ isExpanded }) => (isExpanded ? '2px' : '0')};
    margin-bottom: ${({ isExpanded }) => (isExpanded ? '2px' : '0')};
    max-width: ${({ isExpanded }) => (isExpanded ? '100%' : 'calc(100% - 8px)')};
  }

  .react-select__loading-indicator {
    color: ${colorAccent};
  }
`; // endregion
